// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useDevices } from "../../../Api/newSockets.api";
import { isDeviceSocketLive } from "../../../utils/deviceSocket";
import ChatInterface from "./ChatInterface";

/**
 * Floating messenger-style launcher for the device chat agent. Available on every page
 * (mounted in MainLayout). Opens to a searchable device list; picking one drops into the
 * same ChatInterface used by the full-page Chat tab. Coexists with that tab.
 */
const ChatWidget = () => {
  const { devices } = useDevices();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    const arr = Array.isArray(devices) ? devices : [];
    const q = query.trim().toLowerCase();
    const filtered = q ? arr.filter((d: any) => d.name?.toLowerCase().includes(q)) : arr;
    // Online devices first, then by name.
    return [...filtered].sort((a: any, b: any) => {
      const ao = isDeviceSocketLive(a) ? 0 : 1;
      const bo = isDeviceSocketLive(b) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [devices, query]);

  if (!open) {
    return (
      <button style={S.launcher} onClick={() => setOpen(true)} title="Device Chat">
        <span style={{ fontSize: 20 }}>🤖</span>
        <span style={S.launcherLabel}>Device Chat</span>
      </button>
    );
  }

  return (
    <div style={S.panel}>
      {/* Header */}
      <div style={S.header}>
        {selected ? (
          <button style={S.iconBtn} onClick={() => setSelected(null)} title="Back to devices">←</button>
        ) : (
          <span style={{ fontSize: 18 }}>🤖</span>
        )}
        <div style={S.headerTitle}>
          {selected ? selected.name : "Device Chat"}
        </div>
        <button style={S.iconBtn} onClick={() => setOpen(false)} title="Close">✕</button>
      </div>

      {/* Body */}
      {selected ? (
        <div style={S.chatArea}>
          <ChatInterface deviceId={selected.id} deviceName={selected.name} fill hideHeader />
        </div>
      ) : (
        <div style={S.pickerArea}>
          <input
            style={S.search}
            placeholder="Search devices…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div style={S.deviceList}>
            {list.length === 0 && <div style={S.emptyList}>No devices found.</div>}
            {list.map((d: any) => {
              const online = isDeviceSocketLive(d);
              return (
                <button
                  key={d.id}
                  style={S.deviceRow}
                  onClick={() => setSelected({ id: d.id, name: d.name })}
                >
                  <span style={{ ...S.statusDot, background: online ? "#00ff88" : "#ff3366" }} />
                  <span style={S.deviceName}>{d.name}</span>
                  <span style={S.deviceType}>{d.deviceTypes?.name || ""}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  launcher: {
    position: "fixed", right: 24, bottom: 24, zIndex: 1000,
    height: 48, borderRadius: 24, padding: "0 18px",
    background: "linear-gradient(135deg,#2563eb,#1e40af)",
    border: "none", cursor: "pointer",
    boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", gap: 8,
  },
  launcherLabel: { color: "#fff", fontWeight: 600, fontSize: "0.9rem", whiteSpace: "nowrap" },
  panel: {
    position: "fixed", right: 24, bottom: 24, zIndex: 1000,
    width: 380, height: 560, maxHeight: "calc(100vh - 48px)",
    background: "#12161a", border: "1px solid #232a31", borderRadius: 14,
    boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 14px", borderBottom: "1px solid #232a31",
    background: "linear-gradient(135deg,#151a1f,#1a2026)",
  },
  headerTitle: { color: "#e6edf3", fontWeight: 600, fontSize: "0.95rem", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  iconBtn: { background: "transparent", color: "#8a9199", border: "none", cursor: "pointer", fontSize: "1.1rem", padding: "2px 6px", borderRadius: 6 },
  chatArea: { flex: 1, minHeight: 0, display: "flex" },
  pickerArea: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: 12 },
  search: {
    background: "#0d1114", color: "#e6edf3", border: "1px solid #2c343c",
    borderRadius: 8, padding: "10px 12px", fontSize: "0.85rem", outline: "none", marginBottom: 10,
  },
  deviceList: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 },
  emptyList: { color: "#6c757d", fontSize: "0.85rem", textAlign: "center", marginTop: 20 },
  deviceRow: {
    display: "flex", alignItems: "center", gap: 10,
    background: "#171b20", border: "1px solid #232a31", borderRadius: 8,
    padding: "10px 12px", cursor: "pointer", textAlign: "left",
  },
  statusDot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  deviceName: { color: "#e6edf3", fontSize: "0.88rem", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  deviceType: { color: "#6c757d", fontSize: "0.72rem", fontFamily: "monospace" },
};

export default ChatWidget;
